export type ReorderEdge = "top" | "bottom" | "left" | "right" | "before" | "after";
export type DragAxis = "vertical" | "horizontal";

type ReorderWithEdgeArgs<T extends string | number> = {
  orderedIds: T[];
  sourceId: T;
  targetId: T;
  targetEdge: ReorderEdge;
};

export function reorderWithEdge<T extends string | number>({
  orderedIds,
  sourceId,
  targetId,
  targetEdge,
}: ReorderWithEdgeArgs<T>): { nextOrder: T[]; changed: boolean } {
  if (sourceId === targetId) return { nextOrder: orderedIds, changed: false };

  const sourceIndex = orderedIds.indexOf(sourceId);
  const targetIndex = orderedIds.indexOf(targetId);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
    return { nextOrder: orderedIds, changed: false };
  }

  const insertAfter = targetEdge === "bottom" || targetEdge === "right" || targetEdge === "after";
  const nextOrder = [...orderedIds];
  const [moved] = nextOrder.splice(sourceIndex, 1);

  const baseInsertionIndex = insertAfter ? targetIndex + 1 : targetIndex;
  const adjustedInsertionIndex =
    sourceIndex < baseInsertionIndex ? baseInsertionIndex - 1 : baseInsertionIndex;
  const boundedInsertionIndex = Math.max(0, Math.min(adjustedInsertionIndex, nextOrder.length));
  nextOrder.splice(boundedInsertionIndex, 0, moved);

  const changed = nextOrder.some((id, index) => id !== orderedIds[index]);
  return { nextOrder, changed };
}

export function edgeFromPoint(args: {
  axis: "vertical";
  point: number;
  rectStart: number;
  rectSize: number;
}): "top" | "bottom";
export function edgeFromPoint(args: {
  axis: "horizontal";
  point: number;
  rectStart: number;
  rectSize: number;
}): "left" | "right";
export function edgeFromPoint(args: {
  axis: DragAxis;
  point: number;
  rectStart: number;
  rectSize: number;
}): "top" | "bottom" | "left" | "right" {
  const midpoint = args.rectStart + args.rectSize / 2;
  if (args.axis === "vertical") return args.point < midpoint ? "top" : "bottom";
  return args.point < midpoint ? "left" : "right";
}
